import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { Progress } from "../ui/progress";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { InvestorProfileSettings } from "./InvestorProfileSettings";
import { Target, TrendingUp, Briefcase, User, RefreshCw, Calendar, DollarSign, PieChart, Activity } from "lucide-react";
import { investorService } from "../../services/investorService";
import { activityLogService } from "../../services/activityLogService";
import type { PortfolioAccountPerformanceDTO } from "../../interface/PortfolioAccountPerformanceDTO";

interface InvestorOverviewProps {
  userId?: string;
  riskTolerance?: string;
  portfolioSize?: string;
  investmentExperience?: string;
  investmentGoals?: string[];
  sectors?: string[];
}

export function InvestorOverview({ userId, riskTolerance, portfolioSize, investmentExperience, investmentGoals, sectors }: InvestorOverviewProps) {
  const [performanceData, setPerformanceData] = useState<PortfolioAccountPerformanceDTO[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!userId) return;
    setLoading(true);
    investorService.getPortfolioPerformance(userId, "ACTIVE")
      .then(res => {
        const data = res?.data || res || [];
        setPerformanceData(Array.isArray(data) ? data : []);
      })
      .catch(() => setPerformanceData([]))
      .finally(() => setLoading(false));
  }, [userId]);

  const totalInvested = performanceData.reduce((sum, acc) => sum + (parseFloat(acc.totalInvestedAmount) || 0), 0);
  const totalCurrentValue = performanceData.reduce((sum, acc) => sum + (parseFloat(acc.totalCurrentValue) || 0), 0);
  const realizedGain = totalCurrentValue - totalInvested;
  const totalReturnsPct = totalInvested > 0 ? ((totalCurrentValue - totalInvested) / totalInvested) * 100 : 0;

  const formatCurrency = (value: number) =>
    value.toLocaleString("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 0, maximumFractionDigits: 0 });

  const formatPercent = (value: number) =>
    (value >= 0 ? "+" : "") + value.toFixed(1) + "%";

  const getRiskToleranceLabel = (value: string) => {
    const labels: Record<string, string> = {
      conservative: "Conservative",
      moderate: "Moderate",
      aggressive: "Aggressive",
    };
    return labels[value] || "Moderate";
  };

  const getRiskToleranceValue = (value: string) => {
    const values: Record<string, number> = {
      conservative: 30,
      moderate: 60,
      aggressive: 90,
    };
    return values[value] || 60;
  };

  const getPortfolioSizeLabel = (value: string) => {
    const labels: Record<string, string> = {
      "under-50k": "Under $50,000",
      "50k-250k": "$50,000 - $250,000",
      "250k-1m": "$250,000 - $1,000,000",
      "1m-5m": "$1,000,000 - $5,000,000",
      "over-5m": "Over $5,000,000",
    };
    return labels[value] || "Not specified";
  };

  const getExperienceLabel = (value: string) => {
    const labels: Record<string, string> = {
      beginner: "Beginner (0-2 years)",
      intermediate: "Intermediate (3-5 years)",
      experienced: "Experienced (6-10 years)",
      expert: "Expert (10+ years)",
    };
    return labels[value] || "Not specified";
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Target className="h-5 w-5" />
              Investment Profile
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <div className="flex justify-between mb-2">
                <span className="text-sm font-semibold">Risk Tolerance</span>
                <Badge variant="secondary">
                  {getRiskToleranceLabel(riskTolerance?.toLowerCase() || "")}
                </Badge>
              </div>
              <Progress value={getRiskToleranceValue(riskTolerance?.toLowerCase() || "")} />
            </div>

            <div>
              <div className="flex justify-between mb-2">
                <span className="text-sm font-semibold">Portfolio Size</span>
              </div>
              <p className="text-sm font-medium">
                {getPortfolioSizeLabel(portfolioSize)}
              </p>
            </div>

            <div>
              <div className="flex justify-between mb-2">
                <span className="text-sm font-semibold">Investment Experience</span>
              </div>
              <p className="text-sm font-medium">
                {getExperienceLabel(investmentExperience?.toLowerCase() || "")}
              </p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              Performance Summary
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {loading ? (
              <p className="text-sm text-muted-foreground">Loading performance data...</p>
            ) : performanceData.length === 0 ? (
              <p className="text-sm text-muted-foreground">No portfolio data available</p>
            ) : (
              <>
                <div className="flex justify-between items-center p-3 border rounded-lg">
                  <span className="text-sm font-semibold">Total Returns</span>
                  <span className={totalReturnsPct >= 0 ? "text-green-600" : "text-red-600"}>{formatPercent(totalReturnsPct)}</span>
                </div>
                <div className="flex justify-between items-center p-3 border rounded-lg">
                  <span className="text-sm font-semibold">Total Invested</span>
                  <span>{formatCurrency(totalInvested)}</span>
                </div>
                <div className="flex justify-between items-center p-3 border rounded-lg">
                  <span className="text-sm font-semibold">Current Value</span>
                  <span>{formatCurrency(totalCurrentValue)}</span>
                </div>
                <div className="flex justify-between items-center p-3 border rounded-lg">
                  <span className="text-sm font-semibold">Realized Gains</span>
                  <span className={realizedGain >= 0 ? "text-green-600" : "text-red-600"}>{realizedGain >= 0 ? "+" : ""}{formatCurrency(realizedGain)}</span>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Target className="h-5 w-5" />
            Investment Goals
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            {investmentGoals && investmentGoals.length > 0 ? (
              investmentGoals.map((goal: string) => (
                <Badge key={goal} variant="outline">
                  {goal}
                </Badge>
              ))
            ) : (
              <>
                <Badge variant="outline">Long-term growth</Badge>
                <Badge variant="outline">Retirement planning</Badge>
                <Badge variant="outline">Tax optimization</Badge>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Briefcase className="h-5 w-5" />
            Sectors of Interest
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            {sectors && sectors.length > 0 ? (
              sectors.map((sector: string) => (
                <Badge key={sector} variant="secondary">
                  {sector}
                </Badge>
              ))
            ) : (
              <>
                <Badge variant="secondary">Technology</Badge>
                <Badge variant="secondary">Healthcare</Badge>
                <Badge variant="secondary">Finance</Badge>
                <Badge variant="secondary">Real Estate</Badge>
              </>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

interface InvestorActivityProps {
  userId?: string;
}

export function InvestorActivity({ userId }: InvestorActivityProps) {
  const [activities, setActivities] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!userId) return;
    setLoading(true);
    activityLogService.getActivityLogsByUser(userId)
      .then(res => {
        const data = res?.data || res || [];
        setActivities(Array.isArray(data) ? data : []);
      })
      .catch(() => setActivities([]))
      .finally(() => setLoading(false));
  }, [userId]);

  const getActivityIcon = (type?: string) => {
    const t = type?.toLowerCase() || "";
    if (t.includes("rebalanc") || t.includes("trade") || t.includes("buy") || t.includes("sell"))
      return { icon: RefreshCw, bg: "bg-green-100 dark:bg-green-900", color: "text-green-600 dark:text-green-300" };
    if (t.includes("meeting") || t.includes("call") || t.includes("appointment"))
      return { icon: Calendar, bg: "bg-blue-100 dark:bg-blue-900", color: "text-blue-600 dark:text-blue-300" };
    if (t.includes("goal") || t.includes("target"))
      return { icon: Target, bg: "bg-purple-100 dark:bg-purple-900", color: "text-purple-600 dark:text-purple-300" };
    if (t.includes("deposit") || t.includes("withdrawal") || t.includes("transfer") || t.includes("payment"))
      return { icon: DollarSign, bg: "bg-orange-100 dark:bg-orange-900", color: "text-orange-600 dark:text-orange-300" };
    if (t.includes("alloc") || t.includes("portfolio"))
      return { icon: PieChart, bg: "bg-indigo-100 dark:bg-indigo-900", color: "text-indigo-600 dark:text-indigo-300" };
    return { icon: Activity, bg: "bg-gray-100 dark:bg-gray-800", color: "text-gray-600 dark:text-gray-300" };
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return "";
    try {
      return new Date(dateStr).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
    } catch {
      return dateStr;
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Recent Activity</CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <p className="text-sm text-muted-foreground">Loading activities...</p>
        ) : activities.length === 0 ? (
          <p className="text-sm text-muted-foreground">No recent activity</p>
        ) : (
          <div className="space-y-4">
            {activities.map((a: any, i: number) => {
              const { icon: Icon, bg, color } = getActivityIcon(a.activityType);
              return (
                <div key={a.id || i} className="flex items-start gap-3 pb-3 border-b last:border-b-0">
                  <div className={`p-2 rounded-full ${bg}`}>
                    <Icon className={`h-4 w-4 ${color}`} />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm">{a.description || a.activityType}</p>
                    <p className="text-xs text-muted-foreground">{formatDate(a.createdAt || a.timestamp)}</p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

interface InvestorSettingsProps {
  riskTolerance?: string;
  portfolioSize?: string;
  investmentExperience?: string;
  investmentGoals?: string[];
  sectors?: string[];
}

export function InvestorSettings({ riskTolerance, portfolioSize, investmentExperience, investmentGoals, sectors }: InvestorSettingsProps) {
  return (
    <div className="space-y-6">
      <InvestorProfileSettings riskTolerance={riskTolerance} portfolioSize={portfolioSize} investmentExperience={investmentExperience} investmentGoals={investmentGoals} sectors={sectors} />
      {/* Notifications and Security & Privacy sections hidden for later stage */}
    </div>
  );
}
